import {
  BENTO_CARD_CLICK_SELECTOR,
  bindHomepageCardClicks,
  trackBentoCardClick
} from '../analytics/homepage-cards';
import {
  mountBrightcoveBackgroundPlayer,
  type BrightcoveBackgroundHandle
} from './shared-brightcove-embed';

export type BentoBackgroundVideoPlaybackState = {
  inView: boolean;
  pageVisible: boolean;
  reducedMotion: boolean;
};

export function shouldPlayBentoBackgroundVideo({
  inView,
  pageVisible,
  reducedMotion
}: BentoBackgroundVideoPlaybackState): boolean {
  return inView && pageVisible && !reducedMotion;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function mount(root: HTMLElement): void {
  const unbindCardClicks = bindHomepageCardClicks(root, {
    selector: BENTO_CARD_CLICK_SELECTOR,
    track: trackBentoCardClick
  });
  const players = new Map<HTMLElement, { handle: BrightcoveBackgroundHandle; inView: boolean }>();
  let pageVisible = !document.hidden;
  let reducedMotion = prefersReducedMotion();

  const syncPlayback = (handle: BrightcoveBackgroundHandle, inView: boolean): void => {
    if (
      shouldPlayBentoBackgroundVideo({
        inView,
        pageVisible,
        reducedMotion
      })
    ) {
      void handle.play().catch(() => {
        /* Autoplay can be blocked; poster/image remains visible. */
      });
      return;
    }

    handle.pause();
  };

  const syncAllPlayers = (): void => {
    players.forEach(({ handle, inView }) => {
      syncPlayback(handle, inView);
    });
  };

  const visibilityObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              const host = entry.target as HTMLElement;
              const player = players.get(host);

              if (!player) return;

              player.inView = entry.isIntersecting;
              syncPlayback(player.handle, player.inView);
            });
          },
          { threshold: 0.15 }
        )
      : undefined;

  root.querySelectorAll<HTMLElement>('[data-bento-background-video]').forEach(host => {
    const handle = mountBrightcoveBackgroundPlayer(host, {
      autoplay: false,
      frameClassName: 'bento-card__video-frame video-js vjs-fill',
      loop: true
    });

    if (!handle) {
      return;
    }

    const inView = !visibilityObserver;
    players.set(host, { handle, inView });
    visibilityObserver?.observe(host);
    syncPlayback(handle, inView);
  });

  const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
  const onMotionPreferenceChange = (): void => {
    reducedMotion = reducedMotionMedia.matches;
    syncAllPlayers();
  };
  const onVisibilityChange = (): void => {
    pageVisible = !document.hidden;
    syncAllPlayers();
  };

  reducedMotionMedia.addEventListener('change', onMotionPreferenceChange);
  document.addEventListener('visibilitychange', onVisibilityChange);

  const disconnectObserver = new MutationObserver(() => {
    if (!root.isConnected) {
      disconnectObserver.disconnect();
      visibilityObserver?.disconnect();
      reducedMotionMedia.removeEventListener('change', onMotionPreferenceChange);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      unbindCardClicks();
      players.forEach(({ handle }) => handle.dispose());
      players.clear();
    }
  });

  disconnectObserver.observe(document.body, { childList: true, subtree: true });
}
